import React, { SFC, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { UserModel } from '../../../shared/types/user'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import {
    Grid,
    Paper,
    IconButton,
    Button,
    Tabs,
    Typography,
    Tab,
    CircularProgress,
    Card,
    CardContent,
    ListItemIcon,
    Menu,
    MenuItem,
    ListItemText,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import QueueIcon from '@material-ui/icons/Queue'
import DriveEtaIcon from '@material-ui/icons/DriveEta'
import { TabPanel } from '../../../shared/components/tabPanel'
import { fetchSessionsForUser } from '../../../redux/actions/users'
import { ChargingSession, ChargingSessionState } from '../../../shared/types/chargingSession'
import moment, { Moment } from 'moment'
import { Image } from '../../../shared/components/Image'
import { NoData } from '../../../shared/components/NoData'
import { consumedKwh } from '../../../shared/utils/display'
import { exportFile } from '../../../shared/utils/export'
import { config, getToken } from '../../../config'
import UserAddEditDialog from '../userAddEditDialog/UserAddEditDialog'
import { ChargerConnectorSocketType, getChargerConnectorSocketTypes } from '../../../shared/types/charger'
import { TagModel } from '../../../shared/types/tag'
import { TenantModel } from '../../../shared/types/tenant'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles((theme) => ({
    cardWrapper: {
        height: '100%',
        width: `calc(100% - ${theme.spacing(1)})`,
        paddingLeft: theme.spacing(1),
        '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    cardWrapperInner: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tabContentWrapper: {
        flexGrow: 1,
        overflowY: 'auto',
        height: '100%',
        padding: theme.spacing(3),
    },
    tabContent: {
        height: '100%',
    },
    card: {
        height: '100%',
    },
    titleWrapper: {
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        flex: '0',
    },
    titleText: {
        textAlign: 'left',
    },
    titleButtons: {
        textAlign: 'right',
    },
    profileItem: {
        textAlign: 'left',
    },
    profileValue: {
        paddingLeft: theme.spacing(1),
        textAlign: 'left',
        position: 'relative',
        width: '80%',
        '& span': {
            position: 'absolute',
            top: theme.spacing(0.3),
        },
    },
    groupDivider: {
        height: theme.spacing(1.5),
    },
    sessionDayTitle: {
        marginLeft: theme.spacing(1),
    },
    sessionDayTitleDivider: {
        marginTop: theme.spacing(3),
    },
    sessionListItem: {
        margin: theme.spacing(1, 0, 1, 0),
        padding: theme.spacing(2),
        display: 'flex',
        width: '98%',
    },
    sessionListContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    sessionListContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    sessionImageWrapper: {
        flex: '1 0 auto',
        textAlign: 'right',
        marginLeft: theme.spacing(1),
    },
    sessionImage: {
        width: '80px',
        height: '80px',
    },
    sessionLoadMore: {
        marginBottom: theme.spacing(3),
    },
    exportButton: {
        marginRight: theme.spacing(1),
    },
    fullHeight: {
        height: '100%',
    },
    dropdownIcon: {
        minWidth: '32px',
    }
}))

interface SessionCardProps {
    session: ChargingSession
}
type SessionPaymentStatus = 'free' | 'paid' | 'unpaid' | 'roaming'
const SessionCard: SFC<SessionCardProps> = React.memo(({ session }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const stateLabels = {
        [ChargingSessionState.Pending]: 'Pending',
        [ChargingSessionState.InProgress]: 'In Progress',
        [ChargingSessionState.Parking]: 'Parking',
        [ChargingSessionState.Finished]: 'Finished',
        [ChargingSessionState.FailedToSetChargingProfile]: 'Chyba',
    }

    const paymentStatusTranslation: Record<SessionPaymentStatus, string> = {
        free: 'users.sessionFree',
        paid: 'users.sessionPaid',
        unpaid: 'users.sessionUnpaid',
        roaming: 'users.sessionRoaming',
    }

    const onExport = () => {
        exportFile(
            config.proxy,
            config.api,
            `ChargingSession/invoice/${session.id}`,
            getToken(),
            true
        )
    }

    const formatDuration = () => {
        const duration = moment.duration(
            moment(session.endedAt).diff(moment(session.startedAt))
        )

        return moment()
            .hours(duration.hours())
            .minutes(duration.minutes())
            .seconds(duration.seconds())
            .format('H[h] m[m] s[s]')
    }

    const getSessionPrice = (session: ChargingSession): number => {
        if (session.price > 0) {
            return session.price
        }

        return (session.chargingPrice ?? 0) + (session.parkingPrice ?? 0)
    }

    const getPaymentIntervalLabel = (session: ChargingSession): string => {
        return session.paymentInterval === 1
            ? t('users.monthlyBilling')
            : ''
    }

    const getSessionPaymentStatus = (
        session: ChargingSession
    ): SessionPaymentStatus => {
        const price = getSessionPrice(session)

        if (session.whitelistAuthentication) {
            return 'free'
        }

        if (price === 0) {
            return 'free'
        }

        if (session.paymentStatus != null) {
            switch (session.paymentStatus) {
                case 0:
                    return 'free'
                case 1:
                    return 'paid'
                case 2:
                    return 'unpaid'
                case 3:
                    return 'roaming'
            }
        }

        return session.paid ? 'paid' : 'unpaid'
    }

    const sessionPrice = getSessionPrice(session)
    const paymentStatus = getSessionPaymentStatus(session)
    const paymentIntervalLabel = getPaymentIntervalLabel(session)

    return (
        <Card className={classes.sessionListItem} elevation={1}>
            <div className={classes.sessionListContentWrapper}>
                <CardContent className={classes.sessionListContent}>
                    <Typography variant="h6">
                        {session.location?.name}
                    </Typography>

                    <Typography component="div" variant="caption">
                        {t('users.sessionStart', {
                            time: moment(session.startedAt).format('LT'),
                        })}
                    </Typography>

                    <Typography component="div" variant="caption">
                        {session.endedAt
                            ? t('users.sessionDuration', {
                                duration: formatDuration(),
                                consumedKwh: consumedKwh(session),
                            })
                            : t('users.sessionInProgress')}
                    </Typography>

                    <Typography component="div" variant="caption">
                        {t('history.charger')}: {session.chargerExternalId} |{' '}
                        {t('history.connector')}: {session.connectorExternalId}
                    </Typography>

                    <Typography component="div" variant="caption">
                        {t('users.state')}: {stateLabels[session.state]}
                    </Typography>

                    <Typography component="div" variant="caption">
                        {t('users.price')}: {sessionPrice.toFixed(2)} € |{' '}
                        {t(paymentStatusTranslation[paymentStatus])}
                        {paymentIntervalLabel ? ` | ${paymentIntervalLabel}` : ''}
                    </Typography>
                </CardContent>
            </div>

            <div className={classes.sessionImageWrapper}>
                {session.hasInvoice && (
                    <IconButton
                        className={classes.exportButton}
                        onClick={onExport}
                    >
                        <PictureAsPdfIcon />
                    </IconButton>
                )}

                <Image
                    className={classes.sessionImage}
                    imageClassName={classes.sessionImage}
                    url={
                        session.location?.images?.length > 0
                            ? session.location.images[0].url
                            : undefined
                    }
                />
            </div>
        </Card>
    )
})

interface UserCardProps {
    user: UserModel
    onDelete: Function
    onEdit: Function
    onEditCars: Function
}
const UserCard: SFC<UserCardProps> = ({ user, onDelete, onEdit, onEditCars }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const sessionsState = useSelector((state: any) => state.users.sessions)
    const allTags = useSelector((state: any) => state.tags.items.user.value)
    const allTenants = useSelector((state: any) => state.tenants.value)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const loadRfids = () => {
        let rfidString = ''
        user.rfids?.forEach((rfidObj, index) => {
            rfidString += rfidObj.rfid
            if (user.rfids && index < user.rfids.length - 1) {
                rfidString += ', '
            }
        })
        return rfidString
    }

    const loadTags = () => {
        if (user.tags?.length && allTags.length) {
            const tags = allTags.filter((tagObj: TagModel) => user.tags?.some((userTag) => userTag.tagId === tagObj.id))
            return tags.map((tagObj: TagModel) => tagObj.name).join(', ')
        }
    }

    const loadTenants = () => {
        if (allTenants != null && user.tenants) {
            return user.tenants
                .split(',')
                .map((id) => id.trim())
                .map((id) => allTenants.find((t: TenantModel) => t.id === id))
                .filter(Boolean)
                .map((t) => t.name)
                .join(', ')
        }
        return ''
    }

    const socketTypeLabels = getChargerConnectorSocketTypes(t)

    const mapSocketTypes = (types: ChargerConnectorSocketType[]) => {
        return types.map((type) => socketTypeLabels.find((socket) => socket.id === type)?.label || type).join(', ')
    }

    const [tabIndex, setTabIndex] = useState<number>(0)
    const userValues = [
        [t('users.firstName'), user.firstName],
        [t('users.lastName'), user.lastName],
        [t('users.email'), user.email],
        [t('users.phone'), user.phone],
        [],
        [t('users.rfid'), loadRfids()],
        [t('users.tags'), loadTags()],
        [t('users.tenants'), loadTenants()],
        [],
        [
            t('users.monthlyAllowance'),
            (user.monthlyAllowance ?? 0) > 0
                ? `${(user.monthlySpent ?? 0)} / ${user.monthlyAllowance} €`
                : '0 €',
        ],
        [t('users.address')],
        [t('users.street'), user.street],
        [t('users.city'), user.city],
        [t('users.zip'), user.zip],
        [t('users.state'), user.country],
        [t('users.companyInformation')],
        [t('users.companyName'), user.companyName],
        [t('users.companyId'), user.companyId],
        [t('users.taxId'), user.taxId],
        [t('users.vatId'), user.vatId],
        [],
        [t('users.card')],
        [t('users.cardNumber'), user.cardNumber ? `**** **** **** ${user.cardNumber}` : ''],
        [t('users.expirationDate'), user.cardExpiration],
    ]

    const [addEditDialogState, setAddEditDialogState] = useState({
        open: false,
        isAdd: true,
        onClose: (user: UserModel) => { },
        initialModel: undefined as UserModel | undefined,
    })

    const openAddDialog = () => {
        setAddEditDialogState({
            open: true,
            isAdd: true,
            onClose: onAddDialogClose,
            initialModel: user,
        })
    }
    const onAddDialogClose = () => {
        setAddEditDialogState({ ...addEditDialogState, open: false })
    }

    const sessionsWithDate: any[] = []
    let lastStartedAtDay: Moment
    sessionsState.data.forEach((x: ChargingSession) => {
        const currentStartedAtDay = moment(x.startedAt).startOf('day')
        if (!currentStartedAtDay.isSame(lastStartedAtDay)) {
            sessionsWithDate.push({ date: currentStartedAtDay.format('LL'), value: null })
            lastStartedAtDay = currentStartedAtDay
        }

        sessionsWithDate.push({ date: null, value: x })
    })

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (tabIndex === 1 && sessionsState.executed && sessionsState.key !== user.id) {
            dispatch(fetchSessionsForUser(user, sessionsState))
        }
    }, [dispatch, tabIndex, user, sessionsState])

    return (
        <div className={classes.cardWrapper}>
            <Paper elevation={1} className={classes.card}>
                <div className={classes.cardWrapperInner}>
                    <Grid item xs={12} container className={classes.titleWrapper}>
                        <Grid item xs={10} className={classes.titleText}>
                            <Typography variant="h2" style={{ padding: 0 }}>
                                {user.firstName} {user.lastName}
                            </Typography>
                        </Grid>
                        <Grid className={classes.titleButtons}>
                            <Button
                                onClick={handleMenuOpen}
                                endIcon={<ExpandMoreIcon />}
                            >
                                Actions
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem
                                    onClick={() => {
                                        openAddDialog();
                                        handleMenuClose();
                                    }}
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><QueueIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Duplicate user" />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        onEdit();
                                        handleMenuClose();
                                    }}
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><EditIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Edit User" />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        onEditCars();
                                        handleMenuClose();
                                    }}
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><DriveEtaIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Manage cars" />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        onDelete();
                                        handleMenuClose();
                                    }}
                                    style={{ color: 'red' }} // zvýraznenie Delete
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><DeleteIcon fontSize="small" style={{ color: 'red' }} /></ListItemIcon>
                                    <ListItemText primary="Remove user" />
                                </MenuItem>
                            </Menu>
                            {addEditDialogState.open && <UserAddEditDialog {...addEditDialogState} />}
                        </Grid>
                    </Grid>
                    <div>
                        <Tabs value={tabIndex} onChange={(event: any, newValue: number) => setTabIndex(newValue)}>
                            <Tab label={t('users.profile')} color="primary" />
                            <Tab label={t('users.history')} />
                            <Tab label={t('users.cars')} />
                        </Tabs>
                    </div>
                    <div className={classes.tabContentWrapper}>
                        <TabPanel value={tabIndex} index={0} className={classes.tabContent}>
                            <Grid container className={classes.fullHeight}>
                                {userValues.map((x: any, index) =>
                                    x.length === 2 ? (
                                        <React.Fragment key={index}>
                                            <Grid item sm={3} className={classes.profileItem}>
                                                <Typography variant="subtitle2">{x[0]}</Typography>
                                            </Grid>
                                            <Grid item sm={9} className={classes.profileValue}>
                                                <Typography variant="caption" component="div">
                                                    {x[1] && x[1] !== '' ? x[1] : '\u00A0'}
                                                </Typography>
                                            </Grid>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment key={index}>
                                            <Grid item sm={12} className={classes.groupDivider} />
                                            {x.length === 1 && (
                                                <Grid item sm={12} className={classes.profileItem}>
                                                    <Typography variant="h6">{x[0]}</Typography>
                                                </Grid>
                                            )}
                                        </React.Fragment>
                                    )
                                )}
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tabIndex} index={1} className={classes.tabContent}>
                            {sessionsWithDate.map((sessionWithDate: any, index) =>
                                sessionWithDate.date ? (
                                    <Typography
                                        variant="subtitle2"
                                        key={sessionWithDate.date}
                                        className={`${classes.sessionDayTitle} ${index > 0 ? classes.sessionDayTitleDivider : ''
                                            }`}
                                    >
                                        {sessionWithDate.date}
                                    </Typography>
                                ) : (
                                    <SessionCard key={sessionWithDate.value.id} session={sessionWithDate.value} />
                                )
                            )}

                            {sessionsState.hasMorePages && sessionsState.executed && (
                                <Button
                                    className={classes.sessionLoadMore}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        dispatch(fetchSessionsForUser(user, sessionsState))
                                    }}
                                >
                                    {t('common.loadMore')}
                                </Button>
                            )}
                            {!sessionsState.executed ? (
                                <CircularProgress />
                            ) : (
                                sessionsWithDate.length === 0 && <NoData text={t('users.noSessions')} />
                            )}
                        </TabPanel>
                        <TabPanel value={tabIndex} index={2} className={classes.tabContent}>
                            <Grid container spacing={2}>
                                {user.cars?.map((car: any, index: number) => (
                                    <Grid item xs={12} md={6} key={index}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {/* Brand je povinný, model nemusí byť */}
                                                    {`${car.brand} ${car.model ?? ''}`.trim()}
                                                    {user.cars && user.cars.length > 1 && car.default
                                                        ? ` (${t('users.default')})`
                                                        : ''}
                                                </Typography>

                                                {/* EČV - ak nie je, zobrazíme pomlčku alebo text */}
                                                <Typography variant="body2">
                                                    <strong>{t('users.licensePlate')}:</strong>{' '}
                                                    {car.carLicensePlate || '---'}
                                                </Typography>

                                                <Typography variant="body2">
                                                    <strong>{t('users.socketType')}:</strong>{' '}
                                                    {car.socketType?.length > 0
                                                        ? mapSocketTypes(car.socketType)
                                                        : t('users.noSocket')}
                                                </Typography>

                                                <Typography variant="body2">
                                                    <strong>{t('users.batteryCapacity')}:</strong>{' '}
                                                    {car.batteryCapacity ?? '---'} kWh
                                                </Typography>

                                                {/* Dynamické zobrazenie AC/DC len ak existujú a sú > 0 */}
                                                {car.maxAcPower > 0 && (
                                                    <Typography variant="body2">
                                                        <strong>{t('users.maxAcPower')}:</strong> {car.maxAcPower} kW
                                                    </Typography>
                                                )}

                                                {car.maxDcPower > 0 && (
                                                    <Typography variant="body2">
                                                        <strong>{t('users.maxDcPower')}:</strong> {car.maxDcPower} kW
                                                    </Typography>
                                                )}

                                                {car.year && (
                                                    <Typography variant="body2">
                                                        <strong>{t('users.year')}:</strong> {car.year}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </TabPanel>
                    </div>
                </div>
            </Paper>
        </div>
    )
}

export default UserCard
